import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { useScreenType } from '@gitbook/spine-react';

/**
 * Layout style for the diff viewer.
 * - `stacked`: stacked, single-column view (added/deleted/modified intermixed inline).
 * - `split`: side-by-side view with the base on the left and the head on the right.
 */
export type DiffLayout = 'stacked' | 'split';

/**
 * Options for diffing.
 */
export const diffOptionsState = atomWithStorage(
    'diffOptions',
    {
        /** Only show the changed pages */
        onlyShowChangedPages: true,
        /** Layout style for the diff viewer */
        layout: 'split' as DiffLayout,
    },
    undefined,
    { getOnInit: true }
);

export type UseDiffLayoutResult = {
    /** Layout currently in use. */
    layout: DiffLayout;
    setLayout: (layout: DiffLayout) => void;
    hasSplitDiffs: boolean;
    /** False on mobile, where the layout is always stacked and switching it does nothing. */
    canSplit: boolean;
};

type DiffOptions = {
    onlyShowChangedPages: boolean;
    /** Optional: persisted options written before the layout choice existed omit it. */
    layout?: DiffLayout;
};

/**
 * Resolve the effective diff layout from the persisted options, defaulting to split.
 */
export function getEffectiveDiffLayout(diffOptions: DiffOptions): DiffLayout {
    return diffOptions.layout ?? 'split';
}

/**
 * Effective diff layout + setter.
 *
 * Split is the layout users get by default; their choice from the segmented control is
 * persisted and wins over it. Mobile is always stacked: two columns don't fit on a phone.
 */
export function useDiffLayout(): UseDiffLayoutResult {
    const [diffOptions, setDiffOptions] = useAtom(diffOptionsState);
    const { isMobile } = useScreenType();
    const layout = isMobile ? 'stacked' : getEffectiveDiffLayout(diffOptions);
    const setLayout = (value: DiffLayout) => {
        setDiffOptions({ ...diffOptions, layout: value });
    };
    return { layout, setLayout, hasSplitDiffs: layout === 'split', canSplit: !isMobile };
}
